import { useEffect, useMemo, useState } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import SelectTagInputWithCheckboxes from '@/components/SelectTagInputWithCheckboxes'
import { timelineService } from '@/services/timeline'
import { TimelinePost } from '@/types/post'
import { useData } from '@/contexts/data'
import { useToast } from '@/hooks/use-toast'
import { Check } from 'lucide-react'

const formSchema = z.object({
  territories: z.array(z.string()).min(1, 'Territórios é obrigatório'),
  topics: z.array(z.string()).optional(),
  contexts: z.array(z.string()).optional(),
  type: z.enum(['COMMUNITY', 'CREATOR', 'FORMAT', 'TREND', 'AESTHETIC', 'SUBJECT'], {
    required_error: 'Tipo de Expressão é obrigatório'
  }),
  partners: z.array(z.string()).optional()
})

type FormData = z.infer<typeof formSchema>

type Option = {
  id: string
  name: string
}

const useTerritoryOptions = () => {
  const { territories, territoriesLoading } = useData()
  const options = useMemo(() => territories.map((t) => ({ id: t.id, name: t.name })), [territories])
  return { options, loading: territoriesLoading }
}

const useContextOptions = () => {
  const { contexts, contextsLoading } = useData()
  const options = useMemo(() => contexts.map((c) => ({ id: c.id, name: c.name })), [contexts])
  return { options, loading: contextsLoading }
}

const useTopicOptions = () => {
  const { topics, topicsLoading } = useData()
  const options = useMemo(() => topics.map((t) => ({ id: t.id, name: t.name })), [topics])
  return { options, loading: topicsLoading }
}

const usePartnerOptions = () => {
  const { partners, partnersLoading } = useData()
  const options = useMemo(() => partners.map((p) => ({ id: p.id, name: p.name })), [partners])
  return { options, loading: partnersLoading }
}

const typeOptions = [
  { value: 'COMMUNITY', label: 'Comunidade' },
  { value: 'CREATOR', label: 'Criador' },
  { value: 'FORMAT', label: 'Formato' },
  { value: 'TREND', label: 'Tendência' },
  { value: 'AESTHETIC', label: 'Estética' },
  { value: 'SUBJECT', label: 'Assunto' }
]

interface ApprovePostModalProps {
  open: boolean
  onOpenChange: (_open: boolean) => void
  post: TimelinePost
  onApprove: () => Promise<void>
}

export function ApprovePostModal({ open, onOpenChange, post, onApprove }: ApprovePostModalProps) {
  const { options: territoryOptions, loading: territoriesLoading } = useTerritoryOptions()
  const { options: contextOptions, loading: contextsLoading } = useContextOptions()
  const { options: topicOptions, loading: topicsLoading } = useTopicOptions()
  const { options: partnerOptions, loading: partnersLoading } = usePartnerOptions()
  const { toast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const defaultValues = useMemo(() => ({
    territories: post.territories || [],
    topics: post.topics || [],
    contexts: post.contexts || [],
    type: (post.type as 'COMMUNITY' | 'CREATOR' | 'FORMAT' | 'TREND' | 'AESTHETIC' | 'SUBJECT') || undefined,
    partners: (post.metadata as any)?.partners || []
  }), [post])

  const {
    watch,
    setValue,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: 'onChange'
  })

  const territoriesValue = watch('territories') || []
  const topicsValue = watch('topics') || []
  const contextsValue = watch('contexts') || []
  const typeValue = watch('type')
  const partnersValue = watch('partners') || []

  const isFormValid = territoriesValue.length > 0 && !!typeValue

  useEffect(() => {
    if (open) {
      reset(defaultValues)
    }
  }, [open, defaultValues, reset])

  const handleClose = (value: boolean) => {
    if (!value && isSubmitting) {
      return
    }
    onOpenChange(value)
  }

  const onSubmit = async (data: FormData) => {
    try {
      setIsSubmitting(true)
      
      await timelineService.update(post.id, {
        territories: data.territories,
        contexts: data.contexts,
        topics: data.topics,
        type: data.type,
        partners: data.partners
      })
      
      await onApprove()
      
      toast({
        title: 'Post aprovado',
        description: 'O post foi aprovado com sucesso.'
      })
      
      onOpenChange(false)
    } catch {
      toast({
        title: 'Erro ao aprovar',
        description: 'Não foi possível aprovar o post. Tente novamente.',
        variant: 'error'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-xl">
        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-2">
            <label className="text-sm">
              Territórios: <span className="text-red-500">*</span>
            </label>
            <SelectTagInputWithCheckboxes
              value={territoriesValue}
              onChange={(value) => setValue('territories', value, { shouldDirty: true })}
              placeholder="Pesquisar"
              items={territoryOptions}
              loading={territoriesLoading}
              required
              buttonLabel="Adicionar"
            />
            {errors.territories && (
              <p className="text-sm text-red-500">{errors.territories.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm">Tópicos (opcional):</label>
            <SelectTagInputWithCheckboxes
              value={topicsValue}
              onChange={(value) => setValue('topics', value, { shouldDirty: true })}
              placeholder="Pesquisar"
              items={topicOptions}
              loading={topicsLoading}
              buttonLabel="Adicionar"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm">Contextos acionáveis (opcional):</label>
            <SelectTagInputWithCheckboxes
              value={contextsValue}
              onChange={(value) => setValue('contexts', value, { shouldDirty: true })}
              placeholder="Pesquisar"
              items={contextOptions}
              loading={contextsLoading}
              buttonLabel="Adicionar"
            />
          </div>

          <div>
            <hr className="border-gray-200" />
            <div className="space-y-2 py-3">
              <label className="text-sm">Tipo de Expressão: <span className="text-red-500">*</span></label>
              <SelectTagInputWithCheckboxes
                value={typeValue ? [typeValue] : []}
                onChange={(value) => {
                  const newType = value.length > 0 ? value[value.length - 1] : undefined;
                  setValue('type', newType as any, { shouldDirty: true });
                }}
                placeholder="Pesquisar"
                items={typeOptions.map(option => ({ id: option.value, name: option.label }))}
                loading={false}
                buttonLabel="Adicionar"
              />
            </div>
            <hr className="border-gray-200" />
          </div>

          <div className="space-y-2">
            <label className="text-sm">Marca vinculada (opcional):</label>
            <SelectTagInputWithCheckboxes
              value={partnersValue}
              onChange={(value) => setValue('partners', value, { shouldDirty: true })}
              placeholder="Pesquisar"
              items={partnerOptions}
              loading={partnersLoading}
              buttonLabel="Adicionar"
            />
          </div>

          <div className="flex justify-center pt-6">
            <Button
              type="submit"
              disabled={isSubmitting || !isFormValid}
              className="bg-black hover:bg-gray-800 text-white px-6 py-2 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Check className="w-4 h-4" />
              Aprovar Conteúdo
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
